import { ReactElement, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import PrivateLayout from '@/layouts/PrivateLayout';
import { Helmet } from 'react-helmet-async';
import MainContentsLayout from '@/layouts/MainContentsLayout';
import PanelContents from '@/layouts/panelContents/PanelContents';
import Search from '@/features/analysis/seach/Search';
import ScoreCard from '@/ui/scoreCard/ScoreCard';
import Ranking from '@/ui/Ranking';
import DonutChart from '@/features/charts/donutChart/DonutChart';
import NormalVerticalBarChart from '@/features/charts/normalVerticalBarChart/NormalvVerticalBarChart';
import StackedVerticalBarChart from '@/features/charts/stackedVerticalBarChart/StackedVerticalBarChart';
import HoverMenuButton from '@/components/menu/HoverMenuButton';
import { RatioEvents, TransitionEventItems, TransitionEvents } from '@/types/AnalysisResultType';
import { useUser } from '@/hooks/UserContext';
import FirstTimeExperienceManager from '@/features/firstTimeExperience/FirstTimeExperienceManager';
import { apiPostRequest } from '@/utils/apiClient';
import { notifyCalendarScopeError } from '@/utils/calendarScopeError';
import CalendarScopeErrorModal from '@/components/CalendarScopeErrorModal';
import { convertTimeForScorecard } from '@/ui/scoreCard/util/ScoreCard';
import Alert from '@/components/Alert';
import Table from '@/features/table/Table';
import Loading from '@/components/Loading';
import FeedbackForm from '@/features/feedbackForm/FeedbackForm';
import Advertisement from '@/features/advertisement/Advertisement';
import {
    calculateRatioComparison,
    calculateTransitionComparison,
    calculateTransitionRanking,
} from '@/utils/periodComparison';
import { useTranslation } from 'react-i18next';
import {
    getWeekday,
    removeYearFromDate,
    removeZeroPaddingFromDate,
    getHoursAndMinutes,
} from '@/utils/time';
import { getColors } from '@/services/colorService';
import type {
    StackedVerticalBarChartData,
    StackedVerticalBarChartDataset,
    StackedVerticalBarChartTooltipLabelFormatter,
    StackedVerticalBarChartTooltipFooterFormatter,
} from '@/features/charts/stackedVerticalBarChart/util/StackedVerticalBarChart';

/**
 * 分析ページ
 * @returns
 */
function Analysis(): ReactElement {
    /**
     * フック
     */
    const [ratioEvents, setRatioEvents] = useState<RatioEvents>({
        ratioEventItems: [],
        totalTime: 0,
        averageTime: 0,
        totalCount: 0,
        dailyAggregatedData: [],
    });
    const [transitionEvents, setTransitionEvents] = useState<TransitionEvents>({
        transitionEventItems: [],
        totalTime: 0,
        activeDays: 0,
        periodDays: 0,
        activeDaysRatio: 0,
        averageTimePerDay: 0,
    });
    const [previousTransitionEvents, setPreviousTransitionEvents] = useState<
        TransitionEventItems[]
    >([]);
    const { userInfo, isLoading: userLoading } = useUser();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [analysisType, setAnalysisType] = useState<'ratio' | 'transition'>('ratio');
    const [ratioChartView, setRatioChartView] = useState<'stacked' | 'donut'>('stacked');
    const [legendOpen, setLegendOpen] = useState<boolean>(true);
    const legendAnchorRef = useRef<HTMLDivElement | null>(null);
    const [calendarList, setCalendarList] = useState<
        {
            providerId: string;
            providerAccountName: string;
            providerName?: string;
        }[]
    >([]);
    const [scoreTotalTime, setScoreTotalTime] = useState<number>(0);
    const [scoreAverageTime, setScoreAverageTime] = useState<number>(0);
    const [score3value, setScore3value] = useState<number>(0);
    const [rankingItems, setRankingItems] = useState<
        { title: string; percentage: number; totalTime: number }[]
    >([]);
    const [updateTrigger, setUpdateTrigger] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string[]>([]);
    // 検索パラメータを保持するための状態
    const [searchParams, setSearchParams] = useState<{
        startDate: string;
        endDate: string;
        keyword: string;
        color?: { value: string; label: string };
    }>({
        startDate: '',
        endDate: '',
        keyword: '',
    });

    const { t, i18n } = useTranslation('analysis');
    const { t: tCommon } = useTranslation('common');

    const scorecardTimeUnits = useMemo(
        () => ({
            hourUnit: tCommon('time.hourUnit'),
            minuteUnit: tCommon('time.minuteUnit'),
        }),
        [tCommon],
    );

    const ratioChartToggleLabels = useMemo(
        () => ({
            stacked: t('chart.toggle.stacked'),
            donut: t('chart.toggle.donut'),
        }),
        [t],
    );

    const untitledLabel = useMemo(() => t('chart.untitled'), [t]);
    const totalPerDayLabel = useMemo(() => t('chart.tooltip.totalPerDay'), [t]);
    const stackedNoDataLabel = useMemo(() => t('chart.noData'), [t]);

    const formatScorecardTime = (time: number) => convertTimeForScorecard(time, scorecardTimeUnits);

    const colorCacheRef = useRef<{ language: string; map: Map<string, string> } | null>(null);

    const formatStackedDuration = useCallback((minutes: number) => {
        const [hours, mins] = getHoursAndMinutes(minutes);
        const parts: string[] = [];
        if (hours > 0) {
            parts.push(`${hours}h`);
        }
        if (mins > 0 || parts.length === 0) {
            parts.push(`${mins}m`);
        }
        return parts.join('');
    }, []);

    const formatMonthAwareLabel = useCallback(
        (currentDate: string, previousDate?: string) => {
            if (!currentDate) return '';
            const curNorm = currentDate.includes('/')
                ? currentDate.replace(/\//g, '-')
                : currentDate;
            const prevNorm = previousDate
                ? previousDate.includes('/')
                    ? previousDate.replace(/\//g, '-')
                    : previousDate
                : undefined;
            const curMonth = curNorm.split('-')[1];
            const prevMonth = prevNorm?.split('-')[1];
            const trimmed = removeZeroPaddingFromDate(removeYearFromDate(curNorm)); // M-D
            const [m, d] = trimmed.split('-');
            const base = !prevMonth || prevMonth !== curMonth ? `${m}/${d}` : d; // 先頭or月変わりで M/D、それ以外は日
            const weekday = getWeekday(curNorm, i18n.language);
            if (!weekday) return base.replace(/-/g, '/');
            const isJa = i18n.language?.startsWith('ja');
            const shortWeekday = isJa ? weekday.slice(0, 1) : weekday.slice(0, 2);
            const display = base.replace(/-/g, '/');
            return `${display}(${shortWeekday})`;
        },
        [i18n.language],
    );

    /**
     * calendarList更新
     */
    useEffect(() => {
        if (userInfo?.calendarList) {
            setCalendarList(userInfo.calendarList);
        }
    }, [userInfo]);

    const stackedChartData = useMemo<StackedVerticalBarChartData | null>(() => {
        const daily = ratioEvents.dailyAggregatedData ?? [];
        if (!daily.length) {
            return null;
        }

        // 月境界検出のために日付昇順に正規化
        const sortedDaily = [...daily].sort((a, b) => String(a.date).localeCompare(String(b.date)));

        const labels = sortedDaily.map((item, idx) =>
            formatMonthAwareLabel(item.date, idx > 0 ? sortedDaily[idx - 1].date : undefined),
        );
        const dayTotals = sortedDaily.map((item) => Number(item.totalTime ?? 0));
        const length = sortedDaily.length;

        const datasetMap = new Map<
            string,
            { dataset: StackedVerticalBarChartDataset; totalMinutes: number }
        >();

        sortedDaily.forEach((day, dayIndex) => {
            day.eventItems.forEach((event) => {
                const rawKey = event.title ?? '';
                if (!datasetMap.has(rawKey)) {
                    datasetMap.set(rawKey, {
                        dataset: {
                            id: rawKey,
                            label: rawKey.trim().length > 0 ? rawKey : untitledLabel,
                            values: Array(length).fill(0),
                        },
                        totalMinutes: 0,
                    });
                }
                const holder = datasetMap.get(rawKey);
                if (!holder) return;
                const minutes = Number(event.totalTime ?? 0);
                holder.dataset.values[dayIndex] = minutes;
                holder.totalMinutes += minutes;
            });
        });

        const datasets = Array.from(datasetMap.values())
            .filter((entry) => entry.totalMinutes > 0)
            .sort((a, b) => b.totalMinutes - a.totalMinutes)
            .map((entry) => entry.dataset);

        if (!datasets.length) {
            return null;
        }

        return {
            labels,
            totals: dayTotals,
            datasets,
        };
    }, [formatMonthAwareLabel, ratioEvents.dailyAggregatedData, untitledLabel]);

    const resolveStackedColor = useCallback(
        async (label: string) => {
            const key = label?.trim();
            if (!key) {
                return undefined;
            }

            const currentLanguage = i18n.language;
            if (!colorCacheRef.current || colorCacheRef.current.language !== currentLanguage) {
                try {
                    const colorDefinitions = await getColors('all', currentLanguage);
                    const map = new Map<string, string>();
                    colorDefinitions.forEach((color) => {
                        if (color.label) {
                            map.set(color.label, color.hexColor);
                        }
                        if (color.originalLabel) {
                            map.set(color.originalLabel, color.hexColor);
                        }
                    });
                    colorCacheRef.current = { language: currentLanguage, map };
                } catch {
                    colorCacheRef.current = { language: currentLanguage, map: new Map() };
                }
            }

            return colorCacheRef.current?.map.get(key);
        },
        [i18n.language],
    );

    const stackedTooltipLabelFormatter = useCallback<StackedVerticalBarChartTooltipLabelFormatter>(
        ({ label, value }) => {
            const displayLabel = label?.trim().length ? label : untitledLabel;
            const minutes = Math.round(value);
            const durationText = formatStackedDuration(minutes);
            return `${displayLabel}: ${durationText}`;
        },
        [formatStackedDuration, untitledLabel],
    );

    const stackedTooltipFooterFormatter =
        useCallback<StackedVerticalBarChartTooltipFooterFormatter>(
            ({ total }) => {
                if (total === undefined) {
                    return '';
                }
                return `${totalPerDayLabel}: ${formatStackedDuration(total)}`;
            },
            [formatStackedDuration, totalPerDayLabel],
        );

    const stackedYAxisSuggestedMax = useMemo(() => {
        if (!stackedChartData?.totals?.length) {
            return undefined;
        }
        const max = Math.max(...stackedChartData.totals);
        if (!Number.isFinite(max) || max <= 0) {
            return undefined;
        }
        return max * 1.1;
    }, [stackedChartData]);

    const stackedAxisTickFormatter = useCallback(
        (value: number) => formatStackedDuration(Math.round(value)),
        [formatStackedDuration],
    );

    /**
     * 初期化処理
     * 結果に関するデータを初期化
     */
    const initialize = async () => {
        setRankingItems([]);
        setRatioEvents({
            ratioEventItems: [],
            totalTime: 0,
            averageTime: 0,
            totalCount: 0,
            dailyAggregatedData: [],
        });
        setTransitionEvents({
            transitionEventItems: [],
            totalTime: 0,
            activeDays: 0,
            periodDays: 0,
            activeDaysRatio: 0,
            averageTimePerDay: 0,
        });
        setPreviousTransitionEvents([]);
        setScoreTotalTime(0);
        setScoreAverageTime(0);
        setScore3value(0);
    };

    /**
     * 検索実行（更新ボタン押下）
     * @param searchCalendar
     * @param startTime
     * @param endTime
     * @param searchKeyword
     */
    const handleSearch = async (
        searchCalendar: { value: string; label: string },
        startTime: string,
        endTime: string,
        searchKeyword: string,
        searchColor: { value: string; label: string },
    ) => {
        // ローディング開始
        setIsLoading(true);

        // 検索パラメータを保存（次回検索時にも利用するため）
        setSearchParams({
            startDate: startTime,
            endDate: endTime,
            keyword: searchKeyword,
            color: searchColor,
        });

        // カレンダー情報取得
        try {
            // エラーメッセージを初期化
            setErrorMessage([]);

            // リクエストデータ
            const requestData = {
                providerId: searchCalendar.value,
                startTime: startTime,
                endTime: endTime,
                searchKeywords: searchKeyword ? [searchKeyword] : [],
                color: searchColor?.value || '',
            };

            // APIリクエスト
            const response = await apiPostRequest('/api/analysis/search', requestData);

            // エラーの場合
            if (!response?.ok) {
                // 認証エラーの場合
                if (response?.status === 401 || response?.status === 302) {
                    // ログインページにリダイレクト
                    window.location.replace('/login?error=unauthorized');
                }
                if (response?.status === 403) {
                    notifyCalendarScopeError();
                    setIsLoading(false);
                    return;
                }
                // バリデーションエラー
                if (response?.status === 400) {
                    const errorText = await response.json();
                    const errorList: string[] = Object.values(errorText.errors).flatMap((val) =>
                        Array.isArray(val) ? val : [val],
                    );
                    // エラーメッセージを設定
                    setErrorMessage(errorList);
                    // 各種値を初期化
                    initialize();
                    // ローディング終了
                    setIsLoading(false);
                    return; // 早期リターン追加
                }
                // その他エラーの場合（サーバーエラー）
                window.location.replace('/login?error=server_error');
            }

            const responseData = await response?.json();

            // 今期データと前期データ取得
            const currentData = responseData[0];
            const previousData = responseData.length > 1 ? responseData[1] : null;

            // 分析タイプを判定（色優先）
            // 色が個別指定（''/all 以外）の場合は Transition、
            // それ以外（未指定 or all）の場合は Ratio
            const isSpecificColor =
                searchColor &&
                searchColor.value &&
                searchColor.value !== '' &&
                searchColor.value !== 'all';
            const hasKeywords = !!(searchKeyword && searchKeyword.trim() !== '');
            const newAnalysisType = hasKeywords || isSpecificColor ? 'transition' : 'ratio';
            setAnalysisType(newAnalysisType);

            // データをセット
            // Ratioの場合
            if (newAnalysisType === 'ratio') {
                // 前期データがある場合は前期比較を計算（未定義値は0に丸める）
                const ratioData = previousData
                    ? calculateRatioComparison(currentData, {
                          previousAggregatedDataByEventList: Array.isArray(
                              previousData.aggregatedDataByEventList,
                          )
                              ? previousData.aggregatedDataByEventList
                              : [],
                          previousTotalTime: Number(previousData?.totalTime || 0),
                          previousAverageTime: Number(previousData?.averageTime || 0),
                          previousTotalCount: Number(previousData?.totalCount || 0),
                      })
                    : {
                          // ない場合は、今期データをそのままセット（NaN防止で数値化）
                          ratioEventItems: Array.isArray(currentData?.aggregatedDataByEventList)
                              ? currentData.aggregatedDataByEventList.map((item: any) => ({
                                    title: item?.title ?? '',
                                    totalTime: Number(item?.totalTime || 0),
                                    percentage: Number(item?.percentage || 0),
                                    count: Number(item?.count || 0),
                                    averageTimePerCount: Number(item?.averageTimePerCount || 0),
                                    averageTimePerDay: Number(item?.averageTimePerDay || 0),
                                }))
                              : [],
                          dailyAggregatedData: Array.isArray(currentData?.dailyAggregatedData)
                              ? currentData.dailyAggregatedData.map((item: any) => ({
                                    date: item?.date ?? '',
                                    totalTime: Number(item?.totalTime || 0),
                                    eventItems: Array.isArray(item?.eventItems)
                                        ? item.eventItems.map((dailyItem: any) => ({
                                              title: dailyItem?.title ?? '',
                                              totalTime: Number(dailyItem?.totalTime || 0),
                                              percentage: Number(dailyItem?.percentage || 0),
                                          }))
                                        : [],
                                }))
                              : [],
                          totalTime: Number(currentData?.totalTime || 0),
                          averageTime: Number(currentData?.averageTime || 0),
                          totalCount: Number(currentData?.totalCount || 0),
                      };

                setRatioEvents(ratioData);
                // 合計時間
                setScoreTotalTime(Number(currentData?.totalTime || 0));
                // 平均時間
                setScoreAverageTime(Number(currentData?.averageTime || 0));
                // スケジュール数
                setScore3value(Number(currentData?.totalCount || 0));
                // ランキング
                const sortedItems = [...ratioData.ratioEventItems]
                    .sort((a, b) => b.percentage - a.percentage)
                    .slice(0, 3);
                setRankingItems(
                    sortedItems.map((item) => ({
                        title: item.title,
                        percentage: item.percentage,
                        totalTime: item.totalTime,
                        // 前期比較データ
                        timeComparison: item.totalTimeComparison,
                    })),
                );
            } else {
                // Transitionの場合
                // 前期データがある場合は前期比較を計算
                const transitionData =
                    previousData && Array.isArray(previousData.dateAndTimeList)
                        ? calculateTransitionComparison(
                              currentData,
                              previousData.dateAndTimeList as TransitionEventItems[],
                          )
                        : {
                              // ない場合は、今期データをそのままセット
                              transitionEventItems: Array.isArray(currentData.dateAndTimeList)
                                  ? [...currentData.dateAndTimeList]
                                        .map((item: any) => ({
                                            date: item.date,
                                            time: item.time,
                                        }))
                                        .sort((a, b) => a.date.localeCompare(b.date))
                                  : [],
                              totalTime: currentData.totalTime || 0,
                              activeDays: currentData.activeDays || 0,
                              periodDays: currentData.periodDays || 0,
                              activeDaysRatio: currentData.activeDaysRatio || 0,
                              averageTimePerDay: currentData.averageTimePerDay || 0,
                          };

                setTransitionEvents(transitionData);

                // 前期データの抽出（グラフ用）
                if (
                    'previousDateAndTimeList' in transitionData &&
                    transitionData.previousDateAndTimeList &&
                    Array.isArray(transitionData.previousDateAndTimeList) &&
                    transitionData.previousDateAndTimeList.length > 0
                ) {
                    setPreviousTransitionEvents(transitionData.previousDateAndTimeList);
                } else {
                    setPreviousTransitionEvents([]);
                }

                // 合計時間
                setScoreTotalTime(Number(currentData?.totalTime || 0));
                // 平均時間
                setScoreAverageTime(Number(currentData?.averageTimePerDay || 0));
                // 活動日数
                setScore3value(Number(currentData?.activeDays || 0));
                // ランキング
                setRankingItems(
                    calculateTransitionRanking(
                        transitionData.transitionEventItems,
                        'previousDateAndTimeList' in transitionData
                            ? transitionData.previousDateAndTimeList
                            : undefined,
                    ),
                );
            }

            // 更新トリガー 毎回true/falseを反転させる
            setUpdateTrigger((prev) => !prev);

            // ローディング終了
            setIsLoading(false);
        } catch (error) {
            // ログインページにリダイレクト（サーバーエラー）
            window.location.replace('/login?error=server_error');
        }
    };

    /**
     * パネル設定
     * 各コンポーネントを呼び出す
     */
    const componentMap = useMemo(() => {
        return {
            // 検索
            anSearch: (
                <Search
                    handleSearch={handleSearch}
                    calendarList={calendarList}
                    initialSearchParams={searchParams}
                />
            ),
            // 合計時間
            anTotalTime: (
                <ScoreCard
                    title={t('cards.totalTime')}
                    score={formatScorecardTime(scoreTotalTime)}
                    comparison={
                        analysisType === 'ratio'
                            ? ratioEvents.totalTimeComparison
                            : transitionEvents.totalTimeComparison
                    }
                    comparisonValueType="time"
                    emptyLabel={t('cards.noData')}
                />
            ),
            // 平均時間
            anAverageTime: (
                <ScoreCard
                    title={t('cards.averageTime')}
                    score={formatScorecardTime(scoreAverageTime)}
                    comparison={
                        analysisType === 'ratio'
                            ? ratioEvents.averageTimeComparison
                            : transitionEvents.averageTimePerDayComparison
                    }
                    comparisonValueType="time"
                    emptyLabel={t('cards.noData')}
                />
            ),
            // 第３項目
            anTotalSchedule: (
                <ScoreCard
                    title={analysisType === 'ratio' ? t('cards.totalCount') : t('cards.activeDays')}
                    score={
                        analysisType === 'ratio'
                            ? [
                                  {
                                      type: 'number',
                                      value: String(score3value),
                                  },
                              ]
                            : [
                                  {
                                      type: 'number',
                                      value: String(score3value),
                                  },
                                  {
                                      type: 'unit',
                                      value: t('units.days'),
                                  },
                                  {
                                      type: 'unit',
                                      value: `/ ${transitionEvents.periodDays}${t('units.days')}`,
                                  },
                              ]
                    }
                    comparison={
                        analysisType === 'ratio'
                            ? ratioEvents.totalCountComparison
                            : transitionEvents.activeDaysComparison
                    }
                    comparisonValueType="number"
                    emptyLabel={t('cards.noData')}
                />
            ),
            // ランキング
            anRanking: <Ranking rankingItems={rankingItems} />,
            // 広告
            anAd: <Advertisement />,
            // チャート
            anChart:
                analysisType === 'ratio' ? (
                    <div className="flex h-full w-full flex-col ps-2 pb-2 pe-2">
                        <div className="relative flex-1 min-h-[260px] w-full">
                            <div className="absolute top-0 right-0 z-[2]" ref={legendAnchorRef}>
                                <HoverMenuButton
                                    ariaLabel={t('chart.legendColors')}
                                    items={(() => {
                                        const canShowLegend = ratioChartView === 'stacked';
                                        const legendIcon = (
                                            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                                                <rect x="4" y="6" width="4" height="4" fill="#9CA3AF" />
                                                <rect x="4" y="14" width="4" height="4" fill="#D1D5DB" />
                                                <rect x="10" y="6" width="10" height="4" fill="#6B7280" />
                                                <rect x="10" y="14" width="10" height="4" fill="#6B7280" />
                                            </svg>
                                        );
                                        const donutIcon = (
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                                <path d="M12 3a9 9 0 1 0 9 9h-9V3z" fill="#9CA3AF" />
                                                <path d="M12 12h9A9 9 0 0 1 12 21v-9z" fill="#D1D5DB" />
                                            </svg>
                                        );
                                        const stackedIcon = (
                                            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                                                <rect x="3" y="14" width="4" height="7" fill="#4B5563" />
                                                <rect x="10" y="10" width="4" height="11" fill="#9CA3AF" />
                                                <rect x="17" y="6" width="4" height="15" fill="#D1D5DB" />
                                            </svg>
                                        );
                                        const items = [
                                            {
                                                key: 'legend',
                                                label: t('chart.legendColors'),
                                                icon: legendIcon,
                                                onClick: canShowLegend ? () => setLegendOpen((v) => !v) : undefined,
                                                disabled: !canShowLegend,
                                            },
                        {
                                                key: 'toggle-view',
                                                label:
                                                    ratioChartView === 'stacked'
                                                        ? t('chart.toggle.donut')
                                                        : t('chart.toggle.stacked'),
                                                icon: ratioChartView === 'stacked' ? donutIcon : stackedIcon,
                                                onClick:
                                                    ratioChartView === 'stacked'
                                                        ? () => {
                                                              setLegendOpen(false);
                                                              setRatioChartView('donut');
                                                          }
                                                        : () => {
                                                              setRatioChartView('stacked');
                                                              setLegendOpen(true);
                                                          },
                                            },
                                        ];
                                        return items.filter((it) => !it.disabled);
                                    })()}
                                />
                            </div>
                            {ratioChartView === 'stacked' ? (
                                <StackedVerticalBarChart
                                    data={
                                        stackedChartData ?? {
                                            labels: [],
                                            datasets: [],
                                        }
                                    }
                                    resolveColor={resolveStackedColor}
                                    tooltipLabelFormatter={stackedTooltipLabelFormatter}
                                    yAxisSuggestedMax={stackedYAxisSuggestedMax}
                                    formatYAxisTick={stackedAxisTickFormatter}
                                    noDataMessage={stackedNoDataLabel}
                                    hideSwitchButton={true}
                                    hideLegendButton={true}
                                    legendOpen={legendOpen}
                                    onToggleLegend={() => setLegendOpen((v) => !v)}
                                    legendAnchorRef={legendAnchorRef as unknown as React.RefObject<HTMLElement | null>}
                                />
                            ) : (
                                <DonutChart
                                    data={ratioEvents}
                                    hideSwitchButton={true}
                                    onSwitchToStacked={() => {
                                        setRatioChartView('stacked');
                                        setLegendOpen(true);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                ) : (
                    // 推移データの場合は棒グラフ表示
                    <NormalVerticalBarChart
                        data={transitionEvents.transitionEventItems}
                        previousData={
                            previousTransitionEvents.length > 0
                                ? previousTransitionEvents
                                : undefined
                        }
                    />
                ),
            // テーブル
            anTable: (
                <Table
                    tableType={analysisType}
                    value={
                        analysisType === 'ratio'
                            ? ratioEvents.ratioEventItems
                            : transitionEvents.transitionEventItems
                    }
                />
            ),
        };
    }, [
        updateTrigger,
        calendarList,
        ratioEvents,
        transitionEvents,
        previousTransitionEvents,
        searchParams,
        analysisType,
        ratioChartView,
        ratioChartToggleLabels,
        stackedChartData,
        resolveStackedColor,
        stackedTooltipLabelFormatter,
        stackedTooltipFooterFormatter,
        stackedYAxisSuggestedMax,
        stackedAxisTickFormatter,
        stackedNoDataLabel,
        handleSearch,
        formatScorecardTime,
        rankingItems,
        scoreTotalTime,
        scoreAverageTime,
        score3value,
        t,
    ]);

    // レンダリング
    return (
        <FirstTimeExperienceManager userInfo={userInfo}>
            <CalendarScopeErrorModal />
            <Helmet>
                <title>分析 | Kotomil</title>
            </Helmet>

            {userLoading || isLoading ? (
                // ローディング表示
                <Loading />
            ) : (
                <PrivateLayout>
                    <MainContentsLayout>
                        {errorMessage.length > 0 && (
                            <Alert
                                type="danger"
                                messages={errorMessage}
                                className="mx-6 mt-6 p-4 text-center"
                                allowHtml={true}
                            />
                        )}
                        <PanelContents componentMap={componentMap} />
                        <FeedbackForm />
                    </MainContentsLayout>
                </PrivateLayout>
            )}
        </FirstTimeExperienceManager>
    );
}

export default Analysis;
